import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { toSentenceCaseTable } from '../../utils/tableTextFormat';
import { normalizeForNativePdf } from '../../utils/normalizeForNativePdf';
import { PDF_FONT_BOLD, PDF_FONT_REGULAR, pdfFontFamily, sanitizePdfPt } from '../../utils/pdfFontSetup';
import { renderHybridPdfText } from '../../utils/renderHybridPdfText';
import {
  chunkTableRowsByHeight,
  estimatePortraitTableRowHeightPt,
  PDF_PORTRAIT_CONTENT_HEIGHT_PT,
  PORTRAIT_TABLE_HEADER_HEIGHT_PT,
} from '../../utils/pdfTablePagination';
import { PDF_PORTRAIT_USABLE_WIDTH_PT, PDF_TABLE_FOOTER_SAFETY_PT } from '../../utils/protocolPdfLayout';
import {
  expandPortraitTableRowsForPdf,
  getPortraitTableColWidths,
  splitWidePortraitTableRows,
  tableFontSizeForColCount,
} from '../../utils/pdfPortraitTable';
import {
  PDF_PORTRAIT_TABLE_MAX_ROWS_PER_CHUNK,
  PDF_TABLE_CELL_LINE_HEIGHT,
  PDF_TABLE_CELL_PAD_PT,
  PDF_TABLE_CHUNK_HEIGHT_FRACTION,
} from '../../utils/pdfTableLayoutRules';
import PdfTableCaption from './PdfTableCaption';

const BORDER = '#000000';
const CELL_PAD = PDF_TABLE_CELL_PAD_PT;

export const pdfTableStyles = StyleSheet.create({
  table: {
    width: PDF_PORTRAIT_USABLE_WIDTH_PT,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: BORDER,
    marginBottom: 8,
    // Critical: never flex-grow — wrap={true} + grow stretches empty borders.
    flexGrow: 0,
    alignSelf: 'flex-start',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 1,
    borderColor: BORDER,
    flexGrow: 0,
  },
  cell: {
    flexShrink: 0,
    flexGrow: 0,
    padding: CELL_PAD,
    borderRightWidth: 1,
    borderRightColor: BORDER,
    // Do NOT use overflow:'hidden' — it clips wrapped text against borders in react-pdf.
  },
  cellLast: {
    borderRightWidth: 0,
  },
});

const DEFAULT_TABLE_FONT = {
  header: { size: 11, bold: true },
  body: { size: 11, bold: false },
};

function cellTextStyle(fontConfig, useHeaderStyle, colCount) {
  const header = fontConfig?.tableHeader ?? DEFAULT_TABLE_FONT.header;
  const body = fontConfig?.tableCell ?? DEFAULT_TABLE_FONT.body;
  const src = useHeaderStyle ? header : body;
  const size = useHeaderStyle
    ? sanitizePdfPt(src.size, 11)
    : tableFontSizeForColCount(colCount, fontConfig);
  return {
    fontFamily: pdfFontFamily({ bold: src.bold || useHeaderStyle }),
    fontSize: size,
    lineHeight: PDF_TABLE_CELL_LINE_HEIGHT,
  };
}

export function renderTextRuns(cellRuns, textStyle) {
  const runs = Array.isArray(cellRuns) ? cellRuns : [{ text: String(cellRuns ?? '') }];
  const baseStyle = {
    ...textStyle,
    fontFamily: textStyle?.fontFamily || PDF_FONT_REGULAR,
    lineHeight: textStyle?.lineHeight || PDF_TABLE_CELL_LINE_HEIGHT,
  };

  if (runs.length === 1 && !runs[0].bold && !runs[0].italic) {
    const rendered = renderHybridPdfText(runs[0].text, baseStyle, 'cell-');
    return (
      <Text style={baseStyle} wrap>
        {typeof rendered === 'string' ? rendered : rendered}
      </Text>
    );
  }

  return (
    <Text style={baseStyle} wrap>
      {runs.map((run, idx) => {
        const runStyle = {
          ...baseStyle,
          fontFamily: pdfFontFamily({ bold: run.bold, italic: run.italic }),
        };
        const rendered = renderHybridPdfText(run.text, runStyle, `cell-${idx}-`);
        if (typeof rendered === 'string') {
          return (
            <Text key={idx} style={runStyle}>
              {rendered}
            </Text>
          );
        }
        return <React.Fragment key={idx}>{rendered}</React.Fragment>;
      })}
    </Text>
  );
}

function cellRunsToDisplayText(cellRuns) {
  const runs = Array.isArray(cellRuns) ? cellRuns : [{ text: String(cellRuns ?? '') }];
  return normalizeForNativePdf(runs.map((r) => r.text).join('').trim());
}

function renderTableRow(
  row,
  rowIdx,
  colCount,
  colWidths,
  fontConfig,
  renderCell,
  rowStyle,
  rowKey,
) {
  const cells = Array.isArray(row) ? [...row] : [];
  while (cells.length < colCount) cells.push([{ text: ' ' }]);

  return (
    <View
      key={rowKey ?? rowIdx}
      wrap={false}
      style={[pdfTableStyles.row, rowStyle?.(row, rowIdx)]}
    >
      {cells.map((cellContent, cellIdx) => {
        const isColHeader = rowIdx === 0;
        const isRowHeader = rowIdx > 0 && cellIdx === 0;
        const useHeaderStyle = isColHeader || isRowHeader;
        const textStyle = cellTextStyle(fontConfig, useHeaderStyle, colCount);
        let displayRuns = cellContent;
        if (useHeaderStyle) {
          const plain = cellRunsToDisplayText(cellContent);
          displayRuns = [{ text: toSentenceCaseTable(plain) || ' ' }];
        }
        const renderer =
          renderCell ||
          ((runs, rIdx, cIdx, style) => renderTextRuns(runs, style));
        const rawWidth = colWidths[cellIdx] ?? PDF_PORTRAIT_USABLE_WIDTH_PT / colCount;
        const colWidth =
          Number.isFinite(rawWidth) && rawWidth > 0
            ? rawWidth
            : PDF_PORTRAIT_USABLE_WIDTH_PT / Math.max(1, colCount);
        return (
          <View
            key={cellIdx}
            style={[
              pdfTableStyles.cell,
              { width: colWidth },
              cellIdx === colCount - 1 ? pdfTableStyles.cellLast : null,
            ]}
          >
            {renderer(displayRuns, rowIdx, cellIdx, textStyle)}
          </View>
        );
      })}
    </View>
  );
}

/** Generous, fixed reservations added on top of a chunk's own row/header height for
 * content that has no independent height estimate of its own (see PortraitTableChunk).
 *
 * CHUNK_TITLE_GLUE_RESERVE_PT used to be 72 — nearly double the real height of the common
 * case (the two short "Table N-M..." lead-in sentences glued in via ProtocolPdfDocument's
 * backward-walk, each one line at 12pt body + 3pt margin ≈ 17pt, ≈ 34pt total). Verified via
 * bisection against the real content/styles from an actual export (a short "Overall Design"
 * table preceded by ~2 paragraphs of section text) that 72 was tipping the room check the
 * wrong way by a razor-thin 1pt margin (71 shared the page, 72 forced a fresh one) — the
 * oversized reserve was quietly forcing nearly every table in a real document onto its own
 * page even when the preceding content left more than enough room. 40pt keeps a real safety
 * margin above the ~34pt typical need (covers a slightly longer sentence or a single-line
 * section title) without being inflated enough to cause needless relocations.
 */
const CHUNK_CAPTION_RESERVE_PT = 34;
const CHUNK_TITLE_GLUE_RESERVE_PT = 40;

/**
 * One table segment (header + body rows).
 *
 * The table box is wrap={false} (atomic): a JS-computed chunk's row-height budget is only
 * ever an estimate (real @react-pdf/renderer text measurement can diverge from it, e.g. a
 * cell's real wrapped line count coming out taller than estimated). With wrap={true}, an
 * under-estimated chunk can partially render on the current page and let Yoga naturally
 * continue the rest on the next page — but the header row is only emitted once, at the top
 * of the chunk, so that continuation page has no header at all. Making the chunk atomic
 * means Yoga either fits the WHOLE chunk (header + rows) on the current page or relocates
 * the WHOLE thing to a fresh page — the header can never be left behind.
 *
 * That guarantee only holds if react-pdf actually checks whether the WHOLE atomic block
 * fits before committing to place it. Placing minPresenceAhead directly ON this atomic
 * (wrap={false}) View does not do that reliably — verified (real Times-Roman font, real
 * Table 3-2/9-1 content) to NOT stop react-pdf from starting the block mid-page with
 * insufficient room, squashing every row into a single overlapping mess.
 *
 * What DOES work reliably: a separate, zero-height, wrap={true} "probe" View placed
 * immediately BEFORE this atomic block, carrying minPresenceAhead set to the chunk's own
 * estimated total height (see estimatedHeightPt below). A genuinely wrap={true} element is
 * exactly what minPresenceAhead is designed for — react-pdf relocates the probe itself to a
 * fresh page when less than that much room remains, and everything immediately after it
 * (this atomic block) then starts wherever the probe landed. Verified empirically across
 * both a single chunk and 5 sequential probed chunks: chunks pack onto a shared page when
 * there's room, and relocate cleanly with zero squash when there isn't — recovering the
 * page density that an unconditional `break` on every chunk (the previous approach here)
 * gave up. The estimate must stay conservative (over- rather than under-estimate), same
 * philosophy as the row-height estimator: an over-estimate wastes a little room by
 * relocating slightly earlier than strictly necessary; an under-estimate risks the atomic
 * block landing with insufficient real room and squashing.
 */
function PortraitTableChunk({
  chunkIdx,
  headerRow,
  chunk,
  colCount,
  colWidths,
  fontConfig,
  renderCell,
  rowStyle,
  caption,
  sectionTitle,
  estimatedHeightPt,
}) {
  return (
    <>
      <View minPresenceAhead={estimatedHeightPt} wrap style={{ height: 0 }} />
      <View wrap style={{ alignSelf: 'flex-start', flexGrow: 0 }}>
        {chunkIdx === 0 && sectionTitle ? sectionTitle : null}
        {chunkIdx === 0 && caption ? (
          <PdfTableCaption label={caption.label} title={caption.title} />
        ) : null}
        <View style={pdfTableStyles.table} wrap={false}>
          {renderTableRow(headerRow, 0, colCount, colWidths, fontConfig, renderCell, rowStyle, `h-${chunkIdx}`)}
          {chunk.map((row, rowIdx) =>
            renderTableRow(
              row,
              rowIdx + 1,
              colCount,
              colWidths,
              fontConfig,
              renderCell,
              rowStyle,
              `b-${chunkIdx}-${rowIdx}`,
            ),
          )}
        </View>
      </View>
    </>
  );
}

function makeWidthAwareRowEstimator(colWidths, fontSize) {
  return (row) =>
    estimatePortraitTableRowHeightPt(row, {
      colWidths,
      fontSize,
    });
}

function renderPortraitTablePart(rows, fontConfig, renderCell, rowStyle, caption, sectionTitle) {
  if (!rows?.length) return null;

  const colCount = Math.max(...rows.map((row) => (Array.isArray(row) ? row.length : 0)), 1);
  const colWidths = getPortraitTableColWidths(colCount);
  const bodyFontSize = tableFontSizeForColCount(colCount, fontConfig);
  const expandedRows = expandPortraitTableRowsForPdf(rows, colWidths, bodyFontSize);
  const headerRow = expandedRows[0];
  const bodyRows = expandedRows.slice(1);
  const estimateRowHeight = makeWidthAwareRowEstimator(colWidths, bodyFontSize);
  // Real header height — a fixed constant under-reports wrapped multi-line column
  // titles (long lab-parameter / dose-reduction headers), letting the chunker pack
  // rows past what actually fits and squashing the boundary (same bug class as SOA).
  const headerFontSize = sanitizePdfPt(fontConfig?.tableHeader?.size, 11);
  const headerHeight = Math.max(
    PORTRAIT_TABLE_HEADER_HEIGHT_PT,
    estimatePortraitTableRowHeightPt(headerRow, { colWidths, fontSize: headerFontSize }),
  );
  // Keep first chunk modest (caption + mid-page prior content). Later chunks use
  // a fraction of full page height so wrap never has to squash tall segments.
  const pageBudget = Math.floor(
    (PDF_PORTRAIT_CONTENT_HEIGHT_PT - PDF_TABLE_FOOTER_SAFETY_PT) * PDF_TABLE_CHUNK_HEIGHT_FRACTION,
  );
  // Chunk 0's own minPresenceAhead probe (see PortraitTableChunk) can only share a page with
  // whatever prior paragraph/heading content is already there if its estimated height fits in
  // whatever room that content left behind. A chunk sized up to nearly the FULL page budget —
  // same as every later chunk — essentially never fits into "some room after other content"
  // and so always forces a fresh page, stranding whatever room WAS left as permanent blank
  // space (this was the case for tables with several long free-text rows, e.g. an
  // "estimands" table whose first chunk alone came out to ~609pt against a 618pt page budget).
  // Capping chunk 0's target at roughly half the page budget gives it a realistic chance of
  // fitting into typical leftover room. Trade-off: large tables can split into one extra
  // chunk (one extra repeated header row) more often than before.
  const firstChunkTargetHeight = Math.floor(pageBudget * 0.5);
  const firstPageReserved = Math.max(24, pageBudget - firstChunkTargetHeight) + (caption ? 24 : 0);
  const bodyChunks = bodyRows.length
    ? chunkTableRowsByHeight(bodyRows, {
        maxContentHeight: pageBudget,
        headerHeight,
        estimateRowHeight,
        maxRowsPerChunk: PDF_PORTRAIT_TABLE_MAX_ROWS_PER_CHUNK,
        firstPageReserved,
      })
    : [[]];

  // Each chunk's own minPresenceAhead probe (see PortraitTableChunk) decides its placement
  // independently, so — unlike the old `break`-based approach — there's no shared-wrap-parent
  // hazard here and these can be siblings under a bare Fragment without any special handling.
  return (
    <>
      {bodyChunks.map((chunk, chunkIdx) => {
        const estimatedHeightPt =
          headerHeight +
          chunk.reduce((sum, row) => sum + estimateRowHeight(row), 0) +
          10 +
          (chunkIdx === 0 && caption ? CHUNK_CAPTION_RESERVE_PT : 0) +
          (chunkIdx === 0 && sectionTitle ? CHUNK_TITLE_GLUE_RESERVE_PT : 0);
        return (
          <PortraitTableChunk
            key={chunkIdx}
            chunkIdx={chunkIdx}
            headerRow={headerRow}
            chunk={chunk}
            colCount={colCount}
            colWidths={colWidths}
            fontConfig={fontConfig}
            renderCell={renderCell}
            rowStyle={rowStyle}
            caption={caption}
            sectionTitle={chunkIdx === 0 ? sectionTitle : null}
            estimatedHeightPt={estimatedHeightPt}
          />
        );
      })}
    </>
  );
}

/**
 * Renders a table aligned with Word C-Table styles.
 * Wide tables (Section 4 study-parts matrices) are split into column groups.
 *
 * `sectionTitle`, when provided, is rendered INSIDE the first chunk's atomic (wrap={false})
 * block, ahead of the probe that decides that chunk's placement (see PortraitTableChunk) —
 * same technique as SoaPdfTable — so a heading with no other content after it can never be
 * stranded at the bottom of a page while the table's first chunk relocates to the next one.
 * Only pass this when the table is the section's leading content (no paragraph/list rendered
 * before it); otherwise leave the title to render normally, since it already has real content
 * following it on the page.
 */
export function PdfTable({
  rows,
  fontConfig = null,
  renderCell,
  rowStyle,
  caption = null,
  sectionTitle = null,
}) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const tableParts = splitWidePortraitTableRows(rows);
  if (!tableParts.length) return null;

  return (
    <>
      {tableParts.map((partRows, partIdx) =>
        renderPortraitTablePart(
          partRows,
          fontConfig,
          renderCell,
          rowStyle,
          partIdx === 0 ? caption : null,
          partIdx === 0 ? sectionTitle : null,
        ),
      )}
    </>
  );
}

export function PdfTableFullWidthRow({ children, style }) {
  return (
    <View wrap style={[pdfTableStyles.row, style]}>
      <View style={[pdfTableStyles.cell, pdfTableStyles.cellLast, { width: PDF_PORTRAIT_USABLE_WIDTH_PT }]}>
        {children}
      </View>
    </View>
  );
}
